import { useState, useEffect, useCallback } from "react";
import { Sparkles, ArrowRight, Star, Moon as MoonIcon, LogIn, UserPlus, Pencil, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { getPersonalHoroscope } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const localDateString = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const browserTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const formatDate = (isoDate) =>
  new Date(`${isoDate}T12:00:00`).toLocaleDateString(undefined, {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

function useLocalDate() {
  const [date, setDate] = useState(localDateString());
  useEffect(() => {
    const id = setInterval(() => {
      const now = localDateString();
      setDate((prev) => (prev === now ? prev : now));
    }, 60 * 1000);
    return () => clearInterval(id);
  }, []);
  return date;
}

function Header({ date, subtitle }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="flex items-center gap-2">
        <div className="p-2 rounded-lg bg-violet-500/20">
          <Sparkles className="w-4 h-4 text-violet-400" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-violet-200" data-testid="daily-horoscope-title">Today's Horoscope</h3>
          <p className="text-xs text-white/40" data-testid="daily-horoscope-subtitle">{subtitle}</p>
        </div>
      </div>
      <p className="text-xs text-white/50 text-right shrink-0" data-testid="daily-horoscope-date">{formatDate(date)}</p>
    </div>
  );
}

function Extras({ transit_highlight, lucky_elements, energy_rating }) {
  return (
    <>
      {transit_highlight && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-black/20 mb-4" data-testid="daily-horoscope-transit">
          <MoonIcon className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <p className="text-xs text-white/60">
            <span className="text-amber-400 font-medium">Transit: </span>{transit_highlight}
          </p>
        </div>
      )}
      {lucky_elements && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="daily-horoscope-lucky">
          {lucky_elements.color && <span className="px-2 py-1 rounded-full bg-white/5 text-xs text-white/50">Colour · {lucky_elements.color}</span>}
          {lucky_elements.number && <span className="px-2 py-1 rounded-full bg-white/5 text-xs text-white/50">Number · {lucky_elements.number}</span>}
          {lucky_elements.direction && <span className="px-2 py-1 rounded-full bg-white/5 text-xs text-white/50">Direction · {lucky_elements.direction}</span>}
        </div>
      )}
      {energy_rating && (
        <div className="flex items-center gap-2" data-testid="daily-horoscope-energy">
          <span className="text-xs text-white/40">Energy:</span>
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className={`w-3 h-3 ${i <= energy_rating ? "text-amber-400 fill-amber-400" : "text-white/20"}`} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function PersonalReading({ data }) {
  const { theme, insight, suggestion, natal, transit, birth_time_unknown } = data;
  return (
    <div data-testid="daily-horoscope-personal">
      <p className="text-amber-300 text-base font-medium mb-2" data-testid="daily-horoscope-theme">{theme}</p>
      <p className="text-white/80 text-sm leading-relaxed mb-3" data-testid="daily-horoscope-insight">{insight}</p>
      <p className="text-white/70 text-sm leading-relaxed mb-4 pl-3 border-l-2 border-violet-500/40" data-testid="daily-horoscope-suggestion">{suggestion}</p>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-white/40 mb-4" data-testid="daily-horoscope-placements">
        <span>Natal Moon · {natal.moon_nakshatra} / {natal.moon_sign}</span>
        <span>Sun · {natal.sun_sign}</span>
        {natal.ascendant_sign && <span>Ascendant · {natal.ascendant_sign}</span>}
        <span>Transit Moon · {transit.moon_nakshatra} / {transit.moon_sign}</span>
      </div>
      {birth_time_unknown && (
        <p className="flex items-center gap-1.5 text-[11px] text-white/40 mb-4" data-testid="daily-horoscope-time-unknown">
          <Info className="w-3 h-3" /> Birth time unknown — ascendant and house placements are omitted.
        </p>
      )}
      <Extras {...data} />
      <Link to="/daily-activation" className="inline-flex items-center gap-1 mt-4 text-xs text-white/40 hover:text-white/70 transition-colors" data-testid="daily-horoscope-edit-link">
        <Pencil className="w-3 h-3" /> Edit birth details
      </Link>
    </div>
  );
}

function GeneralReading({ data }) {
  if (!data) return null;
  return (
    <div data-testid="daily-horoscope-general">
      <p className="text-white/80 text-sm leading-relaxed mb-4">{data.message}</p>
      <Extras {...data} />
    </div>
  );
}

function SignedOutPrompt({ onSignIn }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl bg-violet-500/10 border border-violet-500/20 mb-4" data-testid="daily-horoscope-signin-prompt">
      <p className="text-sm text-white/80 flex-1">Sign in for your personalized daily horoscope</p>
      <Button size="sm" onClick={onSignIn} className="bg-violet-500/80 hover:bg-violet-500 text-white shrink-0" data-testid="daily-horoscope-signin-btn">
        <LogIn className="w-3.5 h-3.5 mr-1.5" /> Sign in
      </Button>
    </div>
  );
}

function AddDetailsPrompt() {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-4" data-testid="daily-horoscope-add-details-prompt">
      <p className="text-sm text-white/80 flex-1">Add your birth details to unlock a personalized reading</p>
      <Link to="/daily-activation" data-testid="daily-horoscope-add-details-link">
        <Button size="sm" className="bg-amber-400/20 hover:bg-amber-400/30 text-amber-300 border border-amber-400/30 shrink-0">
          <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Add your birth details <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </Link>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="animate-pulse space-y-3" data-testid="daily-horoscope-loading">
      <div className="h-4 bg-violet-800/30 rounded w-1/3"></div>
      <div className="h-3 bg-violet-800/20 rounded w-full"></div>
      <div className="h-3 bg-violet-800/20 rounded w-5/6"></div>
    </div>
  );
}

export default function DailyHoroscopeWidget() {
  const { user, loading: authLoading, loginWithGoogle } = useAuth();
  const date = useLocalDate();
  const [general, setGeneral] = useState(null);
  const [personal, setPersonal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/horoscope/daily?tz=${encodeURIComponent(browserTz())}&date=${date}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setGeneral)
      .catch(() => setGeneral(null));
  }, [date]);

  const loadPersonal = useCallback(async () => {
    setLoading(true);
    try {
      setPersonal(await getPersonalHoroscope(browserTz(), date));
    } catch {
      setPersonal(null);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setPersonal(null);
      setLoading(false);
      return;
    }
    loadPersonal();
  }, [user, authLoading, loadPersonal]);

  const state = !user ? "signed_out" : personal?.has_birth_data ? "personal" : "no_birth_data";
  const subtitle = state === "personal" ? `Personalized for ${user.name?.split(" ")[0] || "you"}` : "General guidance";

  return (
    <div
      className="bg-gradient-to-br from-violet-950/40 to-indigo-900/30 border border-violet-700/40 rounded-2xl p-6 relative overflow-hidden"
      data-testid="daily-horoscope-widget"
      data-state={state}
    >
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-violet-500/10 rounded-full blur-3xl" />
      <Header date={date} subtitle={subtitle} />
      {authLoading || loading ? (
        <Skeleton />
      ) : state === "personal" ? (
        <PersonalReading data={personal} />
      ) : (
        <>
          {state === "signed_out" ? <SignedOutPrompt onSignIn={loginWithGoogle} /> : <AddDetailsPrompt />}
          <GeneralReading data={general} />
        </>
      )}
    </div>
  );
}
